import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Button,
  Table,
  Row,
  Col,
  Statistic,
  Tag,
  Descriptions,
  Space,
} from "antd";
import {
  SettingOutlined,
  LineChartOutlined,
  DollarOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getArticleById, getHistoricPricings } from "../../../api/Services";

import styles from "./ArticlePricingDetail.module.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ArticlePricingDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [articleData, setArticleData] = useState<any>({});

  useEffect(() => {
    if (id) {
      fetchArticleData();
    }
  }, [id]);

  const fetchArticleData = async () => {
    setLoading(true);

    // Hardcodeo para Classic Blue Jeans (id 11)
    if (id === "11") {
      const priceHistory = [
        { date: "2024-07-01", price: 2150, sales: 310 },
        { date: "2024-08-10", price: 2150, sales: 290 },
        { date: "2024-10-01", price: 2200, sales: 250 },
        { date: "2024-12-01", price: 2200, sales: 240 },
        { date: "2025-03-15", price: 2250, sales: 230 },
        { date: "2025-06-01", price: 2200, sales: 245 },
        { date: "2025-07-01", price: 2200, sales: 250 },
      ];
      const totalSales = priceHistory.reduce(
        (sum, item) => sum + item.sales,
        0
      );
      const estimatedRevenue = priceHistory.reduce(
        (sum, item) => sum + item.price * item.sales,
        0
      );
      const averagePrice =
        priceHistory.reduce((sum, item) => sum + item.price, 0) /
        priceHistory.length;
      const bestPerformingPrice = 2150;
      const highestSalesPrice = 2100;
      setArticleData({
        id: 11,
        name: "Classic Blue Jeans",
        description: "Jeans clásicos azul de corte recto",
        brand: { name: "DenimPro" },
        category: { name: "Jeans" },
        defaultPrice: 2200,
        cost: 40,
        currency: { symbol: "$" },
        currentMargin: 2160,
        suggestedPrice: 2250,
        suggestedMargin: (((2250 - 40) / 2250) * 100).toFixed(2),
        elasticity: -1.1,
        inPromotion: true,
        promotions: [
          {
            id: 1,
            name: "MxN 3x2 en Jeans",
            discount: "3x2",
            endDate: "20/07/2025",
          },
        ],
        priceHistory,
        kpis: {
          averageMargin: 2160,
          averagePrice: 2186,
          timeInPromotion: 12,
          salesVolume: totalSales,
          revenue: 540000,
        },
        bestPerformingPrice,
        highestSalesPrice,
      });
      setLoading(false);
      return;
    }

    // Hardcodeo para Basic White T-shirt (id 10)
    if (id === "10") {
      const priceHistory = [
        { date: "2024-07-01", price: 1100, sales: 280 },
        { date: "2024-09-15", price: 1150, sales: 260 },
        { date: "2024-11-01", price: 1200, sales: 220 },
        { date: "2025-01-10", price: 1200, sales: 210 },
        { date: "2025-03-20", price: 1250, sales: 180 },
        { date: "2025-05-01", price: 1200, sales: 200 },
        { date: "2025-07-01", price: 1200, sales: 190 },
      ];
      const totalSales = priceHistory.reduce(
        (sum, item) => sum + item.sales,
        0
      );
      const estimatedRevenue = priceHistory.reduce(
        (sum, item) => sum + item.price * item.sales,
        0
      );
      const averagePrice =
        priceHistory.reduce((sum, item) => sum + item.price, 0) /
        priceHistory.length;
      const bestPerformingPrice = 1100;
      const highestSalesPrice = 1100;
      setArticleData({
        id: 10,
        name: "Basic White T-shirt",
        description: "Remera básica blanca de algodón",
        brand: { name: "CottonWear" },
        category: { name: "T-shirts" },
        defaultPrice: 1200,
        cost: 15,
        currency: { symbol: "$" },
        currentMargin: 1185,
        suggestedPrice: 1150,
        suggestedMargin: (((1150 - 15) / 1150) * 100).toFixed(2),
        elasticity: 1.4,
        inPromotion: false,
        promotions: [],
        priceHistory,
        kpis: {
          averageMargin: 1185,
          averagePrice: 1186,
          timeInPromotion: 0,
          salesVolume: totalSales,
          revenue: 267500,
        },
        bestPerformingPrice,
        highestSalesPrice,
      });
      setLoading(false);
      return;
    }

    try {
      // Obtener información básica del artículo
      const articleResponse = await getArticleById(id!);

      // Obtener historial de precios
      const pricingHistoryResponse = await getHistoricPricings({
        page: 1,
        articleId: parseInt(id!),
      });

      // Procesar historial de precios para crear datos del gráfico
      const priceHistory = pricingHistoryResponse.pricings
        .map((pricing: any) => ({
          date: pricing.startDate || pricing.endDate,
          price: pricing.price || 0,
          sales: Math.floor(Math.random() * 300) + 100, // Mock sales data por ahora
          margin: pricing.margin || 0,
        }))
        .sort(
          (a: any, b: any) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );

      // Calcular margen actual
      const currentMargin =
        articleResponse.defaultPrice && articleResponse.cost
          ? ((articleResponse.defaultPrice - articleResponse.cost) /
              articleResponse.defaultPrice) *
            100
          : 0;

      // Calcular precio sugerido (costo * 1.6)
      const suggestedPrice = articleResponse.cost
        ? articleResponse.cost * 1.6
        : 0;
      const suggestedMargin =
        suggestedPrice && articleResponse.cost
          ? ((suggestedPrice - articleResponse.cost) / suggestedPrice) * 100
          : 0;

      // Calcular KPIs del historial
      const averagePrice =
        priceHistory.length > 0
          ? priceHistory.reduce(
              (sum: number, item: any) => sum + item.price,
              0
            ) / priceHistory.length
          : articleResponse.defaultPrice || 0;

      const averageMargin =
        priceHistory.length > 0
          ? priceHistory.reduce(
              (sum: number, item: any) => sum + (item.margin || 0),
              0
            ) / priceHistory.length
          : currentMargin;

      const totalSales = priceHistory.reduce(
        (sum: number, item: any) => sum + item.sales,
        0
      );
      const estimatedRevenue = priceHistory.reduce(
        (sum: number, item: any) => sum + item.price * item.sales,
        0
      );

      const processedData = {
        ...articleResponse,
        currentMargin: currentMargin.toFixed(2),
        suggestedPrice: suggestedPrice.toFixed(2),
        suggestedMargin: suggestedMargin.toFixed(2),
        elasticity: -1.2, // Hardcoded por ahora hasta conectar modelo predictivo
        inPromotion: false, // Por ahora hardcoded, se puede mejorar con datos reales
        promotions: [], // Se puede llenar con promociones activas del artículo
        priceHistory: priceHistory,
        kpis: {
          averageMargin: averageMargin.toFixed(2),
          averagePrice: averagePrice.toFixed(2),
          timeInPromotion: 15, // Hardcoded por ahora
          salesVolume: totalSales,
          revenue: estimatedRevenue,
        },
      };

      setArticleData(processedData);
    } catch (error) {
      console.error("Error fetching article data:", error);
      // Fallback to mock data in case of error
      const mockData = {
        id: id,
        name: "Artículo no encontrado",
        description: "No se pudo cargar la información del artículo",
        brand: { name: "N/A" },
        category: { name: "N/A" },
        currentPrice: 0,
        cost: 0,
        currency: { symbol: "$" },
        currentMargin: 0,
        suggestedPrice: 0,
        suggestedMargin: 0,
        elasticity: 0,
        inPromotion: false,
        promotions: [],
        priceHistory: [],
        kpis: {
          averageMargin: 0,
          averagePrice: 0,
          timeInPromotion: 0,
          salesVolume: 0,
          revenue: 0,
        },
      };
      setArticleData(mockData);
    }

    setLoading(false);
  };

  const priceHistoryColumns = [
    {
      title: t("Common-Date"),
      dataIndex: "date",
      key: "date",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t("Common-Price"),
      dataIndex: "price",
      key: "price",
      render: (price: number) =>
        `${articleData.currency?.symbol || "$"} ${price.toFixed(2)}`,
    },
    {
      title: t("Common-Sales"),
      dataIndex: "sales",
      key: "sales",
      render: (sales: number) => sales.toLocaleString(),
    },
  ];

  const priceChartData = {
    labels:
      articleData.priceHistory?.map((item: any) =>
        new Date(item.date).toLocaleDateString()
      ) || [],
    datasets: [
      {
        label: t("Common-Price"),
        data: articleData.priceHistory?.map((item: any) => item.price) || [],
        borderColor: "rgb(24, 144, 255)",
        backgroundColor: "rgba(24, 144, 255, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const salesChartData = {
    labels:
      articleData.priceHistory?.map((item: any) =>
        new Date(item.date).toLocaleDateString()
      ) || [],
    datasets: [
      {
        label: t("Common-Sales"),
        data: articleData.priceHistory?.map((item: any) => item.sales) || [],
        borderColor: "rgb(82, 196, 26)",
        backgroundColor: "rgba(82, 196, 26, 0.1)",
        tension: 0.4,
        fill: true,
        yAxisID: "y",
      },
      {
        label: t("Common-Price"),
        data: articleData.priceHistory?.map((item: any) => item.price) || [],
        borderColor: "rgb(250, 140, 22)",
        backgroundColor: "rgba(250, 140, 22, 0.1)",
        tension: 0.4,
        fill: false,
        yAxisID: "y1",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: t("Common-Date"),
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: t("Common-Price"),
        },
      },
    },
  };

  const salesChartOptions = {
    responsive: true,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: t("Common-Date"),
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        title: {
          display: true,
          text: t("Common-Sales"),
        },
      },
      y1: {
        type: "linear" as const,
        display: true,
        position: "right" as const,
        title: {
          display: true,
          text: t("Common-Price"),
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            {t("Common-PricingDetail")}: {articleData.name}
          </h1>
          <p className={styles.subtitle}>{articleData.description}</p>
        </div>
        <Button
          type="primary"
          icon={<SettingOutlined />}
          onClick={() => navigate(`/admin/articles/${id}/view`)}
        >
          {t("Common-ArticleConfiguration")}
        </Button>
      </div>

      <div className={styles.contentWrapper}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card
              title={t("Common-CurrentInformation")}
              className={styles.infoCard}
            >
              <Descriptions column={4} bordered>
                <Descriptions.Item label={t("Common-Brand")}>
                  {articleData.brand?.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-Category")}>
                  {articleData.category?.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-CurrentPrice")}>
                  <span className={styles.priceValue}>
                    {articleData.currency?.symbol}{" "}
                    {articleData.defaultPrice?.toFixed(2)}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-Cost")}>
                  {articleData.currency?.symbol} {articleData.cost?.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-CurrentMargin")}>
                  <span className={styles.marginValue}>
                    {articleData.currentMargin}%
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-SuggestedPrice")}>
                  <span className={styles.suggestedPrice}>
                    {articleData.currency?.symbol} {articleData.suggestedPrice}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-Elasticity")}>
                  <span className={styles.elasticity}>
                    {articleData.elasticity}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t("Common-InPromotion")}>
                  {articleData.inPromotion ? (
                    <Tag color="green">{t("Common-Yes")}</Tag>
                  ) : (
                    <Tag color="red">{t("Common-No")}</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title={t("Common-AverageMargin")}
                    value={articleData.kpis?.averageMargin}
                    precision={2}
                    suffix="%"
                    prefix={<PercentageOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title={t("Common-AveragePrice")}
                    value={articleData.kpis?.averagePrice}
                    precision={2}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title={t("Common-TimeInPromotion")}
                    value={articleData.kpis?.timeInPromotion}
                    suffix="% del tiempo"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title={t("Common-TotalRevenue")}
                    value={articleData.kpis?.revenue}
                    precision={2}
                    prefix={<DollarOutlined />}
                  />
                </Card>
              </Col>
            </Row>
          </Col>

          {articleData.inPromotion && articleData.promotions?.length > 0 && (
            <Col span={24}>
              <Card
                title={t("Common-ActivePromotions")}
                className={styles.promotionsCard}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {articleData.promotions?.map((promo: any) => (
                    <Card
                      key={promo.id}
                      size="small"
                      className={styles.promoCard}
                    >
                      <Row justify="space-between" align="middle">
                        <Col>
                          <strong>{promo.name}</strong>
                          <br />
                          <span>
                            {t("Common-Discount")}: {promo.discount}
                          </span>
                        </Col>
                        <Col>
                          <Tag color="blue">
                            {t("Common-EndDate")}: {promo.endDate}
                          </Tag>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              </Card>
            </Col>
          )}

          <Col span={24}>
            <Card
              title={
                <Space>
                  <LineChartOutlined />
                  {t("Common-PriceHistory")}
                </Space>
              }
              className={styles.chartCard}
            >
              <div className={styles.chartContainer}>
                <Line data={priceChartData} options={chartOptions} />
              </div>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={
                <Space>
                  <LineChartOutlined />
                  {t("Common-SalesImpact")}
                </Space>
              }
              className={styles.chartCard}
            >
              <div className={styles.chartContainer}>
                <Line data={salesChartData} options={salesChartOptions} />
              </div>
              <div className={styles.salesImpactInfo}>
                <div className={styles.impactSummary}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title={t("Common-BestPerformingPrice")}
                        value={
                          articleData.priceHistory?.length > 0
                            ? Math.min(
                                ...articleData.priceHistory.map(
                                  (p: any) => p.price
                                )
                              )
                            : 0
                        }
                        prefix="$"
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title={t("Common-HighestSales")}
                        value={
                          articleData.priceHistory?.length > 0
                            ? Math.max(
                                ...articleData.priceHistory.map(
                                  (p: any) => p.sales
                                )
                              )
                            : 0
                        }
                        suffix={t("Common-Units")}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title={t("Common-PriceElasticity")}
                        value={articleData.elasticity}
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={t("Common-HistoricPricings")}
              className={styles.historyCard}
            >
              <Table
                columns={priceHistoryColumns}
                dataSource={articleData.priceHistory}
                rowKey="date"
                loading={loading}
                pagination={false}
                size="small"
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={t("Common-PriceOptimizationRecommendations")}
              className={styles.recommendationsCard}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <div className={styles.recommendation}>
                  <strong>{t("Common-OptimalPrice")}:</strong>{" "}
                  {articleData.currency?.symbol} {articleData.suggestedPrice}
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ArticlePricingDetail;
